/**
 * Authenticated frame: binds `Layout` (sidebar, header, preview banner) to the API and switches the
 * 14 sections of the reference sidebar (documentation/01_visual_reference/INDEX.md "Main sections").
 * B3 (D-205): Dashboard and Settings are real containers; the other sections render PlaceholderPage.
 * Sidebar keys, icons and permission filter (`./sections.tsx`) follow original/src/App.tsx:25388-25580 (D-124, D-129).
 */
import { ErrorBoundary } from "./ErrorBoundary.js";
import { beginScreen } from "./telemetry.js";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, CreditCard, ShieldCheck } from "lucide-react";
import { AnimatePresence } from "motion/react";
import { CALCULATOR_CHAT_TABS, CalculatorChatActions, CustomersHeaderActions, DynamicIcon, Layout, formatWithCommas, type HeaderNotification, type PreviewBannerData, type SidebarNavItem } from "@faylasuf/ui";
import type { NotificationRow, SessionInfo } from "../api/types.js";
import { ApprovalsContainer, usePendingApprovals } from "../pages/approvals/ApprovalsContainer.js";
import { DashboardPage, type DashboardTarget } from "../pages/DashboardPage.js";
import { PlaceholderPage } from "../pages/PlaceholderPage.js";
import { DollarPage } from "../pages/DollarPage.js";
import { CurrenciesPage } from "../pages/CurrenciesPage.js";
import { CustomersPage } from "../pages/CustomersPage.js";
import { TransfersPage } from "../pages/TransfersPage.js";
import { JournalPage } from "../pages/JournalPage.js";
import { FinancialsPage } from "../pages/FinancialsPage.js";
import { MatchPage } from "../pages/MatchPage.js";
import { AuditPage } from "../pages/AuditPage.js";
import { InstallmentsPage } from "../pages/InstallmentsPage.js";
import { StorePage } from "../pages/StorePage.js";
import { SettingsPage } from "../pages/SettingsPage.js";
import { WalletsPage, useAccounts } from "../pages/WalletsPage.js";
import { QuickTxContainer } from "../pages/wallets/QuickTxContainer.js";
import { useSubscriptionDialog } from "../pages/settings/SubscriptionContainer.js";
import { useApp, useSession } from "./context.js";
import { errorMessage, isSessionLost } from "./errors.js";
import type { UiPrefs } from "./preferences.js";
import { keys } from "./query.js";
import { SECTIONS, orderedSections, sectionVisible, type SectionFlags, type SectionKey } from "./sections.js";
import { useToast } from "./toast.js";
import { useWatchdogWindow } from "./watchdog-window.js";

export type { SectionKey } from "./sections.js";

/** App.tsx:25776-25782: en-US MM-DD-YYYY regardless of language (the metadata's `dateFormat`). */
function formatDateLine(d: Date): string {
  return d.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }).replace(/\//g, "-");
}

function formatTime(iso: string, locale: string, timeZone: string): string {
  try {
    return new Date(iso).toLocaleTimeString(locale === "ar" ? "ar-IQ-u-nu-latn" : "en-US", { hour: "2-digit", minute: "2-digit", timeZone });
  } catch {
    return iso;
  }
}

function notificationKind(n: NotificationRow): HeaderNotification["kind"] {
  if (n.category === "stagnant_debt" || n.code.startsWith("stagnant")) return "stagnant_debt";
  if (n.category === "transaction" || n.category === "operation") return "transaction";
  return "other";
}

/** [C21] The old header title (App.tsx:25755-25772) has no case for these sections: the h2 renders empty (s04 F-246). */
const UNTITLED_SECTIONS: ReadonlySet<string> = new Set(["currencies", "customer_portal", "audit_control", "sub_safes"]);

export function Shell({ prefs, setPreference }: { prefs: UiPrefs; setPreference: (key: string, value: unknown, onError?: (e: unknown) => void) => void }) {
  const { source, now, t, locale, boot } = useApp();
  const { session, hasPermission, logout, sessionLost, isSuperManager } = useSession();
  // [C27] D-261 / D-030: the licence notice — a header chip from 7 days before the cover ends and in read-only states,
  // the subscription dialog behind it, and the payment page instead of the portal while closed / blocked.
  const licence = session.licence ?? null;
  const subscription = useSubscriptionDialog({ gate: licence?.closed === true });
  const watchdogWindow = useWatchdogWindow(); // [C34] D-136: header button on the dashboard, Ctrl+Shift+L everywhere
  const toast = useToast();
  const qc = useQueryClient();
  // [C28] D-259: each section visit is one screen with its own trace id, begun before the section's requests start.
  const [active, setActiveSection] = useState<SectionKey>(() => {
    beginScreen("dashboard");
    return "dashboard";
  });
  const setActiveRaw = (k: SectionKey) => {
    beginScreen(k);
    setActiveSection(k);
  };
  const [target, setTarget] = useState<DashboardTarget | null>(null); // [C26]
  const setActive = (k: SectionKey) => {
    setTarget(null);
    setActiveRaw(k);
  };
  // [C37] F-373 (a): the old rule (App.tsx:1940-1951) — below 1024 px the side menu is collapsed, at 1024 px and wider
  // it is open; applied at start and on every resize (the old app did the same), so a narrow window never shows the
  // full menu over the content.
  const [collapsed, setCollapsed] = useState(() => typeof window !== "undefined" && window.innerWidth < 1024);
  useEffect(() => {
    const onResize = () => setCollapsed(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [search, setSearch] = useState("");
  // C19: quick operation (dashboard), approvals panel, the box to highlight after a charge
  const [quickTx, setQuickTx] = useState<"deposit" | "withdraw" | null>(null);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [highlightBoxId, setHighlightBoxId] = useState<string | null>(null);
  // C22: the debts header's "إضافة جديد" opens the add dialog of the Customer Accounts page.
  const [customersAdd, setCustomersAdd] = useState(0);

  const onError = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };

  // Office settings are readable with the `settings` token only; without it the flags keep their defaults.
  const canReadSettings = hasPermission("settings");
  const settingsQuery = useQuery({ queryKey: keys.settings, queryFn: () => source.settings(), enabled: canReadSettings });
  const settingValue = (key: string): unknown => settingsQuery.data?.[key]?.value;
  const flags: SectionFlags = { usdHidden: settingValue("currencies.usd_dealing_hidden") === true, subSafesActive: settingValue("sub_safes.active") === true, currencyVaultHidden: settingValue("currencies.show_vault") === false, installmentsOff: settingValue("modules.installments") === false, storeOff: settingValue("modules.store") === false };
  const notificationsEnabled = settingValue("notifications.master") !== false;
  const showCurrencyVault = settingValue("currencies.show_vault") !== false;
  const defaultRatePct = typeof settingValue("identity.digital_commission_default_pct") === "string" ? (settingValue("identity.digital_commission_default_pct") as string) : String(settingValue("identity.digital_commission_default_pct") ?? "0.1");

  // C19: the ledger read model feeds the Wallets screen, the quick operation and the dashboard figures.
  const canSeeAccounts = hasPermission("wallets") || hasPermission("view_main_vault_iqd") || hasPermission("view_main_vault_usd") || hasPermission("dollar") || hasPermission("currencies") || hasPermission("sub_safes");
  const accountsQuery = useAccounts(canSeeAccounts);
  const accountNames = new Map((accountsQuery.data ?? []).map((a) => [a.id, a.name ?? a.kind]));
  const approvalsQuery = usePendingApprovals(hasPermission("approve_operations"));
  const pendingCount = (approvalsQuery.data ?? []).filter((p) => p.status === "pending" && p.maker !== session.user.username).length;

  const ratesQuery = useQuery({ queryKey: keys.rates, queryFn: () => source.rates() });
  // [C38] D-062: the Super Manager reading a superseded dataset version sees it on every screen
  const datasetsQuery = useQuery({ queryKey: keys.datasets, queryFn: () => source.datasets(), enabled: isSuperManager, staleTime: 30_000 });
  const viewing = datasetsQuery.data?.viewing ?? null;
  const notificationsQuery = useQuery({ queryKey: keys.notifications, queryFn: () => source.notifications(), enabled: notificationsEnabled, staleTime: 60_000 });
  const queryErrors = [settingsQuery.error, ratesQuery.error, notificationsQuery.error];
  useEffect(() => {
    if (queryErrors.some((e) => e && isSessionLost(e))) sessionLost();
  }, [queryErrors[0], queryErrors[1], queryErrors[2], sessionLost]);

  const dismiss = useMutation({
    mutationFn: (id?: string) => source.dismissNotification(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notifications }),
    onError,
  });

  // Sidebar: reference order, the user's own order (D-124 `ui.sidebar_order`) and the D-129 filter.
  const items: SidebarNavItem[] = orderedSections(prefs.sidebarOrder, hasPermission, flags).map((k) => {
    const def = SECTIONS[k];
    const custom = prefs.icons[k];
    return {
      key: k,
      label: t(def.labelKey),
      icon: custom ? <DynamicIcon name={custom} size={19} /> : def.icon,
      active: active === k,
      onClick: () => {
        setActive(k);
        setSearch("");
      },
    };
  });

  const usd = ratesQuery.data?.find((r) => r.currency === "USD");
  // Strings straight from the API (D-012 unit, no arithmetic here); `price` shows the buy figure, the old
  // "midpoint" had no API equivalent (UNKNOWN — REQUIRES VERIFICATION, see README).
  const rate = flags.usdHidden || !usd ? null : { buy: formatWithCommas(usd.buy), sell: formatWithCommas(usd.sell), price: formatWithCommas(usd.buy) };

  const notifications: HeaderNotification[] = (notificationsQuery.data ?? []).map((n) => {
    const kind = notificationKind(n);
    const params: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(n.params)) if (typeof v === "string" || typeof v === "number") params[k] = v;
    const key = `notif.${n.code}`;
    const message = t(key, params);
    return { id: n.id, title: t(`notif.title.${kind}`), message: message === key ? n.code : message, date: formatDateLine(new Date(n.created_at)), kind };
  });

  // D-223: the catalogue text (`preview.banner`, identical to the API's Arabic literal) so the English UI reads English.
  const banner: PreviewBannerData | null = session.preview ? { text: t("preview.banner"), wipeDate: session.preview.wipeDate ?? t("common.dash") } : null;

  // D-210: hours warning (once, at −10 min) and the grace state (persistent chip in the header).
  const hours = session.session.hours ?? null;
  const warned = useRef(false);
  useEffect(() => {
    if (!hours || hours.grace) return;
    if (new Date(hours.warningAt).getTime() <= Date.now() && !warned.current) {
      warned.current = true;
      toast(t("app.hours_warning", { time: formatTime(hours.endsAt, locale, session.office.timeZone) }), "info");
    }
  }, [hours, toast, t, locale, session.office.timeZone]);
  useIdleWarning(session, (minutesLeft) => toast(t("app.idle_warning", { n: minutesLeft }), "info"), sessionLost);

  const comingB4 = () => toast(t("app.coming_b4"), "info");

  const approvalsChip = pendingCount > 0 ? (
    <button type="button" data-testid="approvals-chip" onClick={() => setApprovalsOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-black">
      <ShieldCheck size={16} />
      {t("approvals.chip", { n: pendingCount })}
    </button>
  ) : null;

  // App.tsx:26228-26303: the calculator & chat header actions of the money tabs (dialogs arrive with D09).
  const tabActions = CALCULATOR_CHAT_TABS.includes(active) ? (
    <CalculatorChatActions themeColor={prefs.accent} unreadChatCount={0} onCalculator={comingB4} onChat={comingB4} />
  ) : active === "debts" ? (
    // App.tsx:26307-26360: the debts header actions (add + internal operator chat; the chat dialog arrives with D09).
    <CustomersHeaderActions themeColor={prefs.accent} unreadChatCount={0} onAdd={() => setCustomersAdd((n) => n + 1)} onChat={comingB4} />
  ) : null;

  const licenceChip = licence && (licence.warn || licence.readOnly) ? (
    <button
      type="button"
      data-testid="licence-chip"
      data-state={licence.state}
      onClick={() => (isSuperManager ? subscription.open() : toast(t("subscription.ask_manager"), "info"))}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black border whitespace-nowrap ${licence.readOnly ? "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400" : "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400"}`}
    >
      <CreditCard size={16} />
      {licence.readOnly ? t("subscription.chip_read_only", { day: licence.lapseDay ?? 0 }) : t("subscription.chip_warn", { n: licence.daysLeft ?? 0 })}
    </button>
  ) : null;

  const headerExtraChip = hours?.grace ? (
    <div data-testid="grace-notice" className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-black">
      <Clock size={16} />
      {t("app.grace_notice", { time: formatTime(hours.graceUntil ?? hours.endsAt, locale, session.office.timeZone) })}
    </div>
  ) : boot.mode === "fixture" && boot.harness.chip !== false ? (
    <div data-testid="fixture-badge" className="px-3 py-2 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400 text-[10px] font-black uppercase tracking-widest">
      {t("fixture.badge", { name: boot.fixture?.name ?? "" })}
    </div>
  ) : licenceChip ?? approvalsChip;
  const headerExtra = tabActions || headerExtraChip ? (
    <div className="flex items-center gap-2.5">
      {headerExtraChip}
      {tabActions}
    </div>
  ) : null;

  let page: ReactNode;
  if (active === "dashboard")
    page = (
      <DashboardPage
        prefs={prefs}
        onComingB4={comingB4}
        onQuickTx={(type) => setQuickTx(type)}
        onOpenSection={(k) => setActive(k)}
        subSafesActive={flags.subSafesActive}
        onOpenTarget={(target) => {
          // [C26] the dashboard's card clicks (App.tsx:26880-26935, 26968-26971): the section opens on the card's tab / filter
          if (!sectionVisible(target.section, hasPermission, flags)) return;
          setTarget(target);
          setActiveRaw(target.section);
        }}
      />
    );
  else if (active === "settings") page = <SettingsPage prefs={prefs} setPreference={setPreference} flags={flags} onComingB4={comingB4} />;
  else if (active === "dollar") page = <DollarPage />;
  else if (active === "currencies" && showCurrencyVault) page = <CurrenciesPage />;
  else if (active === "transfers_cash") page = <TransfersPage />;
  else if (active === "movements") page = <JournalPage />;
  else if (active === "installments" && !flags.installmentsOff) page = <InstallmentsPage />; // [C33] D07 (D-105 – D-109)
  else if (active === "store" && !flags.storeOff) page = <StorePage />; // [C33] D07 (D-110 – D-116)
  else if (active === "match") page = <MatchPage />; // [C32] section 12 (D-120 – D-122, D-298, D-299, D-302 – D-304)
  else if (active === "financials") page = <FinancialsPage key={target?.section === "financials" ? "t" : "f"} {...(target?.section === "financials" ? { initialTab: target.tab } : {})} />;
  else if (active === "debts") page = <CustomersPage key={target?.section === "debts" ? target.filter : "all"} flags={flags} themeColor={prefs.accent} addRequest={customersAdd} {...(target?.section === "debts" ? { initialFilter: target.filter } : {})} />;
  else if (active === "wallets") page = <WalletsPage flags={flags} showCurrencyVault={showCurrencyVault} onNavigate={(k) => setActive(k)} highlightBoxId={highlightBoxId} />;
  else if (active === "audit_control")
    // [C34] section 14 (D-134, D-135). The approvals entry stays above the tabs for makers and for approvers with
    // something waiting (V-95: an approver with nothing pending reaches the panel from the header chip as elsewhere).
    page = (
      <div className="space-y-4">
        {(!hasPermission("approve_operations") || pendingCount > 0) && (
          <button type="button" data-testid="open-approvals" onClick={() => setApprovalsOpen(true)} className="w-full premium-card p-6 flex items-center justify-between text-right">
            <span className="font-black text-sm dark:text-[#f0f6fc] flex items-center gap-2"><ShieldCheck className="text-amber-500" size={18} />{t("approvals.title")}</span>
            <span className="text-xs font-bold text-gray-400 dark:text-[#8b949e]">{t("approvals.open_hint")}</span>
          </button>
        )}
        <AuditPage />
      </div>
    );
  else page = <PlaceholderPage section={active} />;

  // [C27] closed / blocked: nothing but the payment page (the API refuses every other route, LICENCE_CLOSED).
  if (licence?.closed) return <div data-testid="licence-gate" className="min-h-screen bg-slate-950">{subscription.node}</div>;

  return (
    <Layout
      officeName={session.office.name}
      username={session.user.username}
      rate={rate}
      onRateClick={() => (sectionVisible("dollar", hasPermission, flags) ? setActive("dollar") : undefined)}
      items={items}
      collapsed={collapsed}
      onToggleCollapsed={() => setCollapsed((c) => !c)}
      themeColor={prefs.accent}
      onLogout={() => void logout()}
      onSupport={comingB4}
      banner={banner}
      pageTitle={UNTITLED_SECTIONS.has(active) ? "" : active === "installments" ? t("header.installments") /* [C33] old title "قسم الأقساط والبيع بالتقسيط" (V-72 resolved) */ : t(SECTIONS[active].labelKey)}
      dateLine={formatDateLine(now())}
      darkMode={prefs.theme === "dark"}
      onToggleTheme={() => setPreference("ui.theme", prefs.theme === "dark" ? "light" : "dark", onError)}
      notifications={notifications}
      notificationsEnabled={notificationsEnabled}
      onDismissNotification={(id) => dismiss.mutate(id)}
      onNotificationClick={(id) => {
        const n = (notificationsQuery.data ?? []).find((x) => x.id === id);
        if (n?.category === "approvals") setApprovalsOpen(true);
        else if (n?.category === "watchdog") watchdogWindow.open(); // [C34] D-049 alert → the watchdog window
      }}
      onClearNotifications={() => dismiss.mutate(undefined)}
      searchValue={search}
      onSearchChange={setSearch}
      dashboardActions={
        active === "dashboard"
          ? {
              onRefresh: () => void qc.invalidateQueries(),
              isEditing: false,
              onStartEditing: comingB4,
              onDoneEditing: comingB4,
              onCalculator: comingB4,
              onWatchdog: watchdogWindow.allowed ? watchdogWindow.open : comingB4,
              onChat: comingB4,
              unreadChatCount: 0,
            }
          : null
      }
      headerExtra={headerExtra}
    >
      {/* [C30] O-10, D-284: the old <main> holds every section in one AnimatePresence mode="wait" (App.tsx:26374):
          leaving the dashboard plays its exit (opacity 0, scale 0.95) before the next section mounts */}
      {viewing !== null && (
        <div className="mb-4 p-3 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-800 dark:bg-indigo-950/20 dark:border-indigo-900/40 dark:text-indigo-300 text-xs font-black flex items-center justify-between gap-3" data-testid="dataset-view-banner">
          <span>{t("maint.view_banner", { n: viewing })}</span>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            onClick={() => void source.datasetViewClose().then(() => qc.invalidateQueries())}
          >
            {t("maint.view_close")}
          </button>
        </div>
      )}
      <AnimatePresence mode="wait">
        <ErrorBoundary key={active}>{page}</ErrorBoundary>
      </AnimatePresence>
      {quickTx && (
        <QuickTxContainer
          type={quickTx}
          accounts={accountsQuery.data ?? []}
          flags={flags}
          defaultRatePct={defaultRatePct}
          onClose={() => setQuickTx(null)}
          onPosted={(boxId) => {
            setHighlightBoxId(boxId);
            setActive("wallets");
          }}
        />
      )}
      {approvalsOpen && <ApprovalsContainer accountNames={accountNames} onClose={() => setApprovalsOpen(false)} />}
      {subscription.node}
      {watchdogWindow.node}
    </Layout>
  );
}

/**
 * D-039 idle timeout: the server expires the session after `idleMinutes` without a request; the client
 * mirrors it from local activity — warns one minute before and drops to login when the minutes are up
 * (the next request would be refused with SESSION_EXPIRED anyway).
 */
function useIdleWarning(session: SessionInfo, warn: (minutesLeft: number) => void, expire: () => void): void {
  const idleMs = Math.max(1, session.session.idleMinutes) * 60_000;
  const callbacks = useRef({ warn, expire });
  callbacks.current = { warn, expire };
  useEffect(() => {
    let last = Date.now();
    let warned = false;
    const touch = () => {
      last = Date.now();
      warned = false;
    };
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "wheel", "touchstart"];
    for (const ev of events) window.addEventListener(ev, touch, { passive: true });
    const timer = setInterval(() => {
      const left = idleMs - (Date.now() - last);
      if (left <= 0) callbacks.current.expire();
      else if (left <= 60_000 && !warned) {
        warned = true;
        callbacks.current.warn(1);
      }
    }, 10_000);
    return () => {
      clearInterval(timer);
      for (const ev of events) window.removeEventListener(ev, touch);
    };
  }, [idleMs]);
}
